// Traversée récursive de la lignée d'une personne, pour les représentations
// d'export profondes (éventail, ascendance, sablier). Contrairement à
// getFocusFamily.ts (borné à ~2 générations, non récursif), ces fonctions
// remontent/descendent sur un nombre arbitraire de générations.
//
// Sémantique des relations identique à buildTree.ts :
//   - 'parent' / 'grand_parent' / 'arriere_grand_parent' : source = parent, cible = enfant
//   - 'conjoint' / 'ex_conjoint' : couple ; date_debut = mariage, date_fin = divorce
//   - le sexe ('Homme'/'Femme') distingue père/mère ; sinon slots ordonnés.

import { Membre, Relation } from '../types';

const HIERARCHICAL_RELATIONS = ['parent', 'grand_parent', 'arriere_grand_parent'];
const CONJUGAL_RELATIONS = ['conjoint', 'ex_conjoint'];

export interface AncestorNode {
  membre: Membre;
  gen: number;              // 0 = personne racine, 1 = parents, 2 = grands-parents…
  pere: AncestorNode | null; // ascendant "père" (ou 1er slot si sexe inconnu)
  mere: AncestorNode | null; // ascendant "mère" (ou 2e slot si sexe inconnu)
}

export interface DescendantNode {
  membre: Membre;
  gen: number;              // 0 = personne racine
  conjoint: Membre | null;
  children: DescendantNode[];
}

function buildMemberMap(membres: Membre[]): Map<number, Membre> {
  return new Map(membres.map(m => [m.id, m]));
}

// Parents directs d'un membre, répartis en père / mère.
function parentsOf(
  id: number,
  map: Map<number, Membre>,
  relations: Relation[],
): { pere?: Membre; mere?: Membre } {
  const parents = relations
    .filter(r => HIERARCHICAL_RELATIONS.includes(r.type_relation) && r.membre_cible === id)
    .map(r => map.get(r.membre_source))
    .filter((m): m is Membre => !!m);

  const pere = parents.find(p => p.sexe === 'Homme');
  const mere = parents.find(p => p.sexe === 'Femme');
  const rest = parents.filter(p => p !== pere && p !== mere);
  return { pere: pere ?? rest[0], mere: mere ?? rest[1] };
}

/**
 * Arbre d'ascendance récursif centré sur `rootId`, jusqu'à `maxGen` générations.
 * `seen` (par branche) évite les boucles sur des relations cycliques.
 */
export function getAncestors(
  rootId: number,
  membres: Membre[],
  relations: Relation[],
  maxGen: number,
  map: Map<number, Membre> = buildMemberMap(membres),
  gen = 0,
  seen: Set<number> = new Set(),
): AncestorNode | null {
  const membre = map.get(rootId);
  if (!membre || seen.has(rootId)) return null;

  const node: AncestorNode = { membre, gen, pere: null, mere: null };
  if (gen < maxGen) {
    const { pere, mere } = parentsOf(rootId, map, relations);
    const nextSeen = new Set(seen).add(rootId);
    if (pere) node.pere = getAncestors(pere.id, membres, relations, maxGen, map, gen + 1, nextSeen);
    if (mere) node.mere = getAncestors(mere.id, membres, relations, maxGen, map, gen + 1, nextSeen);
  }
  return node;
}

function spouseOf(id: number, map: Map<number, Membre>, relations: Relation[]): Membre | null {
  const rel = relations.find(
    r => CONJUGAL_RELATIONS.includes(r.type_relation) && (r.membre_source === id || r.membre_cible === id),
  );
  if (!rel) return null;
  const otherId = rel.membre_source === id ? rel.membre_cible : rel.membre_source;
  return map.get(otherId) ?? null;
}

function childrenOf(id: number, map: Map<number, Membre>, relations: Relation[]): Membre[] {
  const ids = relations
    .filter(r => r.type_relation === 'parent' && r.membre_source === id)
    .map(r => r.membre_cible);
  return Array.from(new Set(ids)).map(cid => map.get(cid)).filter((m): m is Membre => !!m);
}

/**
 * Arbre de descendance récursif centré sur `rootId`, jusqu'à `maxGen` générations.
 * `seen` (par branche) évite les boucles.
 */
export function getDescendants(
  rootId: number,
  membres: Membre[],
  relations: Relation[],
  maxGen: number,
  map: Map<number, Membre> = buildMemberMap(membres),
  gen = 0,
  seen: Set<number> = new Set(),
): DescendantNode | null {
  const membre = map.get(rootId);
  if (!membre || seen.has(rootId)) return null;

  const node: DescendantNode = { membre, gen, conjoint: spouseOf(rootId, map, relations), children: [] };
  if (gen < maxGen) {
    const nextSeen = new Set(seen).add(rootId);
    node.children = childrenOf(rootId, map, relations)
      .map(c => getDescendants(c.id, membres, relations, maxGen, map, gen + 1, nextSeen))
      .filter((n): n is DescendantNode => !!n);
  }
  return node;
}

/**
 * Identifiants des « racines » de la généalogie : les membres qui ne sont la
 * cible d'aucune relation hiérarchique (donc sans parent connu dans les
 * données). Même logique que `racines` dans buildTree.ts. Sert à exporter
 * TOUTE la famille (tous les troncs), pas seulement la lignée d'une personne.
 */
export function getRootIds(membres: Membre[], relations: Relation[]): number[] {
  const childIds = new Set(
    relations
      .filter(r => HIERARCHICAL_RELATIONS.includes(r.type_relation))
      .map(r => r.membre_cible),
  );
  return membres.filter(m => !childIds.has(m.id)).map(m => m.id);
}

/**
 * Sous-ensemble { membres, relations } contenant `rootId`, TOUS ses descendants
 * (récursivement) et les conjoints rencontrés. Permet de reconstruire, via
 * buildTree, l'arbre descendant d'une seule personne (avec ses couples).
 * On ne remonte PAS les ascendants ni les descendants des conjoints.
 */
export function descendantSubset(
  rootId: number,
  membres: Membre[],
  relations: Relation[],
): { membres: Membre[]; relations: Relation[] } {
  const map = buildMemberMap(membres);
  const keep = new Set<number>();
  const queue: number[] = [];
  if (map.has(rootId)) { keep.add(rootId); queue.push(rootId); }

  while (queue.length) {
    const id = queue.shift()!;
    // Conjoints (ajoutés mais non explorés vers le bas : on ne descend que par la lignée)
    relations
      .filter(r => CONJUGAL_RELATIONS.includes(r.type_relation) && (r.membre_source === id || r.membre_cible === id))
      .forEach(r => {
        const other = r.membre_source === id ? r.membre_cible : r.membre_source;
        if (map.has(other)) keep.add(other);
      });
    // Enfants (explorés récursivement)
    relations
      .filter(r => r.type_relation === 'parent' && r.membre_source === id)
      .forEach(r => {
        const child = r.membre_cible;
        if (map.has(child) && !keep.has(child)) { keep.add(child); queue.push(child); }
      });
  }

  return {
    membres: membres.filter(m => keep.has(m.id)),
    relations: relations.filter(r => keep.has(r.membre_source) && keep.has(r.membre_cible)),
  };
}
